import { useState } from 'react';
import { Paper, useMantineTheme } from '@mantine/core';

interface Node {
  id: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  group?: string;
  tooltip?: string;
}

interface Edge {
  from: string;
  to: string;
  label?: string;
}

const nodes: Node[] = [
  // Users (row 0) - spread wide
  { id: 'mobile', label: 'Mobile App', x: 30, y: 20, w: 120, h: 36, color: '#868e96', tooltip: 'Native Mobile App für Käufer. Greift auf EKS-Backend und WordPress zu.' },
  { id: 'users', label: 'Nutzer / Käufer', x: 220, y: 20, w: 140, h: 36, color: '#868e96', tooltip: 'Endkunden, die Produkte kaufen. Nutzen WordPress-Shop und Seller UI.' },
  { id: 'sellers', label: 'Verkäufer', x: 450, y: 20, w: 120, h: 36, color: '#868e96', tooltip: 'Shop-Betreiber. Verwalten Angebote, Bilder und Bestellungen über die Seller UI.' },
  { id: 'admins', label: 'Admin', x: 700, y: 20, w: 120, h: 36, color: '#868e96', tooltip: 'Administratoren. Zugriff auf Admin UI, Matomo Analytics und WordPress-Backend.' },

  // Frontends (row 1) - WordPress + Matomo together on right
  { id: 'seller-ui', label: 'Seller UI\nseller.u-like-it.de', x: 30, y: 100, w: 160, h: 48, color: '#4c6ef5', tooltip: 'React + Mantine SPA für Verkäufer. Produktverwaltung, KI-Beschreibungen, Bildupload. Keycloak-Auth.' },
  { id: 'admin-ui', label: 'Admin UI\nadmin.u-like-it.de', x: 450, y: 100, w: 160, h: 48, color: '#4c6ef5', tooltip: 'React Admin-Dashboard. Cockpit, Architektur, Monitoring. Cognito-Auth, gehostet auf S3+CloudFront.' },
  { id: 'wordpress', label: 'WordPress\nu-like-it.de', x: 820, y: 100, w: 150, h: 48, color: '#4c6ef5', tooltip: 'CMS für öffentliche Website und Blog. Läuft als Pod in EKS mit MariaDB.' },
  { id: 'matomo', label: 'Matomo\nmatomo.u-like-it.de', x: 1000, y: 100, w: 155, h: 48, color: '#4c6ef5', tooltip: 'Web-Analytics (datenschutzkonform, IP-Anonymisierung). Läuft als Pod in EKS. 9 Ziele, serverseitiges Conversion-Tracking.' },

  // Load Balancers / CDN (row 2)
  { id: 'alb-eks', label: 'ALB (EKS)', x: 60, y: 195, w: 130, h: 34, color: '#f76707', tooltip: 'Application Load Balancer für EKS. SSL-Terminierung, Path-basiertes Routing zu Services.' },
  { id: 'cloudfront', label: 'CloudFront', x: 470, y: 195, w: 130, h: 34, color: '#f76707', tooltip: 'CDN für Admin UI. Statische Assets aus S3, globales Caching, HTTPS.' },
  { id: 'alb-wp', label: 'ALB (WP/Matomo)', x: 880, y: 195, w: 155, h: 34, color: '#f76707', tooltip: 'Application Load Balancer für WordPress und Matomo. Host-basiertes Routing zur EC2-Instanz.' },

  // AWS Services (row 3)
  { id: 'route53', label: 'Route 53\n(DNS)', x: 30, y: 265, w: 120, h: 44, color: '#fab005', tooltip: 'DNS-Management für alle Domains. A-Records, CNAME, Alias zu ALBs und CloudFront.' },
  { id: 'ses', label: 'SES\n(E-Mail)', x: 175, y: 265, w: 105, h: 44, color: '#fab005', tooltip: 'Simple Email Service. Transaktions-E-Mails (Bestellbestätigungen, Passwort-Reset).' },
  { id: 'ecr', label: 'ECR\n(Registry)', x: 305, y: 265, w: 110, h: 44, color: '#fab005', tooltip: 'Elastic Container Registry. Docker Images für alle Microservices und Seller UI.' },
  { id: 's3', label: 'S3\n(Static Assets)', x: 440, y: 265, w: 130, h: 44, color: '#fab005', tooltip: 'Object Storage. Admin UI Frontend, Backups, Produktbilder.' },
  { id: 'cognito', label: 'Cognito\n(Admin Auth)', x: 595, y: 265, w: 135, h: 44, color: '#fab005', tooltip: 'Authentifizierung für Admin UI. User Pool mit MFA, JWT-Token für API Gateway.' },
  { id: 'acm', label: 'ACM\n(Certificates)', x: 755, y: 265, w: 125, h: 44, color: '#fab005', tooltip: 'AWS Certificate Manager. SSL/TLS-Zertifikate für alle Domains, automatische Verlängerung.' },

  // Conversion Tracking (row 3.5)
  { id: 'api-gateway', label: 'API Gateway\n(Admin API)', x: 30, y: 340, w: 140, h: 44, color: '#fab005', tooltip: 'REST API für Admin UI und interne Services. Cognito-Auth für Admin, API-Key für gRPC-Services.' },
  { id: 'lambda-conversions', label: 'Lambda\n(Conversions)', x: 190, y: 340, w: 130, h: 44, color: '#fab005', tooltip: 'Conversion-Tracking Lambda. Idempotente Deduplizierung, Matomo-Ziele, SQS-Queue für Google Ads. Prüft Einwilligung und Click-ID vor dem Upload.' },
  { id: 'dynamodb-conv', label: 'DynamoDB\n(Conversions)', x: 340, y: 340, w: 130, h: 44, color: '#fab005', tooltip: 'Conversion-Tabelle. Speichert alle Business Goals mit Attribution-Daten und Upload-Status.' },
  { id: 'sqs-googleads', label: 'SQS FIFO\n(Google Ads)', x: 490, y: 340, w: 130, h: 44, color: '#fab005', tooltip: 'FIFO Queue für Google Ads Uploads. Async-Verarbeitung mit DLQ für Fehler.' },
  { id: 'lambda-worker', label: 'Lambda Worker\n(Data Manager)', x: 640, y: 340, w: 145, h: 44, color: '#fab005', tooltip: 'Verarbeitet SQS-Queue. Lädt Conversions zu Google Ads Data Manager API hoch.' },
  { id: 'google-ads', label: 'Google Ads\n(Data Manager API)', x: 805, y: 340, w: 155, h: 44, color: '#868e96', tooltip: 'Externes Ziel. 5 der 9 Ziele werden hochgeladen (DemoRequested, MerchantRegistrationCompleted, OfferPublished, FirstQrRedeemed, MerchantActive30Days). Erfordert gclid und erteilte Einwilligung.' },

  // EC2 instances (outside EKS)
  { id: 'ec2-gpu', label: 'EC2 (g5.xlarge)\nGPU Node\nScale-to-Zero', x: 1000, y: 320, w: 170, h: 60, color: '#e64980', tooltip: 'GPU-Instanz (g5.xlarge, NVIDIA A10G). Scale-to-Zero via Cluster Autoscaler. Startet bei Bedarf in ~3 Min.' },

  // EKS Application Services (row 4)
  { id: 'bff', label: 'Seller BFF\n(Express)', x: 30, y: 420, w: 120, h: 44, color: '#20c997', group: 'eks', tooltip: 'Backend-for-Frontend (Node.js/Express). Proxy zu gRPC-Services, KI-Endpunkt für Produktbeschreibungen.' },
  { id: 'seller-nginx', label: 'Seller UI\n(nginx)', x: 170, y: 420, w: 110, h: 44, color: '#20c997', group: 'eks', tooltip: 'nginx Container. Serviert Seller UI SPA, Proxy zu BFF API, 50MB Upload-Limit.' },
  { id: 'storage', label: 'Storage\n(gRPC/Go)', x: 300, y: 420, w: 115, h: 44, color: '#20c997', group: 'eks', tooltip: 'Zentraler Datenzugriff (gRPC/Go). CRUD für Angebote, Bilder, Kategorien. PostgreSQL + etcd.' },
  { id: 'authorization', label: 'Authorization\n(gRPC/Go)', x: 435, y: 420, w: 130, h: 44, color: '#20c997', group: 'eks', tooltip: 'Berechtigungsprüfung (gRPC/Go). Rollen- und Rechteverwaltung für Verkäufer.' },
  { id: 'payment', label: 'Payment\n(gRPC/Go)', x: 585, y: 420, w: 115, h: 44, color: '#20c997', group: 'eks', tooltip: 'Zahlungsabwicklung (gRPC/Go). Integration mit Payment-Providern, Transaktionsverwaltung.' },
  { id: 'mailer', label: 'Mailer\n(gRPC/Go)', x: 720, y: 420, w: 110, h: 44, color: '#20c997', group: 'eks', tooltip: 'E-Mail-Versand (gRPC/Go). Templates, Warteschlange. Sendet über AWS SES.' },
  { id: 'recovery', label: 'Recovery\n(Go)', x: 850, y: 420, w: 105, h: 44, color: '#20c997', group: 'eks', tooltip: 'Datenwiederherstellung (Go). Backup-Restore, Konsistenzprüfungen.' },

  // Auth & AI (row 5)
  { id: 'keycloak', label: 'Keycloak\n(Auth)', x: 30, y: 500, w: 120, h: 44, color: '#be4bdb', group: 'eks', tooltip: 'Identity Provider (Keycloak). Realm: ulikeit, OIDC für Seller UI, Benutzerverwaltung.' },
  { id: 'keycloak-cors', label: 'Keycloak\nCORS Proxy', x: 170, y: 500, w: 110, h: 44, color: '#be4bdb', group: 'eks', tooltip: 'nginx CORS Proxy für Keycloak. Ermöglicht Browser-Zugriff von seller.u-like-it.de.' },
  { id: 'ollama', label: 'Ollama\n(GPU Node)', x: 300, y: 500, w: 115, h: 44, color: '#be4bdb', group: 'eks', tooltip: 'LLM-Inference-Server. OpenAI-kompatible API. Läuft auf GPU-Node mit Tolerations.' },
  { id: 'qwen', label: 'Qwen2.5-VL\n(7B Vision LLM)', x: 435, y: 500, w: 130, h: 44, color: '#be4bdb', group: 'eks', tooltip: 'Vision-Language-Modell (7B Parameter, Q4_K_M). Generiert Produktbeschreibungen aus Bildern. ~6GB VRAM.' },

  // Data layer (row 5)
  { id: 'postgres', label: 'PostgreSQL\n(1 Primary + 2 Read)', x: 585, y: 500, w: 165, h: 44, color: '#fd7e14', group: 'eks', tooltip: 'PostgreSQL-Cluster (Zalando Operator). 1 Primary + 2 Read Replicas. Datenbanken: storage, keycloak.' },
  { id: 'etcd', label: 'etcd\n(3 Nodes)', x: 770, y: 500, w: 100, h: 44, color: '#fd7e14', group: 'eks', tooltip: 'Key-Value-Store (3 Nodes). Konfigurationsdaten und Feature-Flags für alle Services.' },
  { id: 'etcd-browser', label: 'etcd Browser\nsettings.u-like-it.de', x: 890, y: 500, w: 155, h: 44, color: '#fd7e14', group: 'eks', tooltip: 'Web-UI für etcd. Konfigurationseinstellungen bearbeiten und einsehen.' },
  { id: 'pgadmin', label: 'pgAdmin\npgadmin.u-like-it.de', x: 1065, y: 500, w: 145, h: 44, color: '#fd7e14', group: 'eks', tooltip: 'PostgreSQL-Administration. Web-UI für Datenbankmanagement und SQL-Queries.' },

  // CMS & Analytics in EKS (row 5.5)
  { id: 'wp-pod', label: 'WordPress\n(Pod)', x: 585, y: 580, w: 120, h: 44, color: '#20c997', group: 'eks', tooltip: 'WordPress-Pod in EKS. mu-plugins per ConfigMap: Attribution-Erfassung und Demo-/Registrierungs-Webhooks.' },
  { id: 'matomo-pod', label: 'Matomo\n(Pod)', x: 725, y: 580, w: 115, h: 44, color: '#20c997', group: 'eks', tooltip: 'Matomo-Pod in EKS. 9 Ziele, trust_visitors_cookies aktiv für serverseitiges Tracking. Archivierung alle 15 Min.' },
  { id: 'mariadb', label: 'MariaDB\n(Pod)', x: 860, y: 580, w: 110, h: 44, color: '#fd7e14', group: 'eks', tooltip: 'MariaDB-Pod. Datenbanken für WordPress und Matomo.' },

  // Monitoring (row 6)
  { id: 'prometheus', label: 'Prometheus\nprometheus.u-like-it.de', x: 30, y: 660, w: 165, h: 44, color: '#e8590c', group: 'eks', tooltip: 'Metriken-Sammlung und Alerting. Scrapet alle Services, Nodes und Exporter. Basic-Auth geschützt.' },
  { id: 'grafana', label: 'Grafana\ngrafana.u-like-it.de', x: 215, y: 660, w: 150, h: 44, color: '#e8590c', group: 'eks', tooltip: 'Monitoring-Dashboards. Visualisierung von Cluster-, Service- und DB-Metriken.' },
  { id: 'alertmanager', label: 'Alertmanager', x: 385, y: 660, w: 115, h: 44, color: '#e8590c', group: 'eks', tooltip: 'Alert-Routing und -Gruppierung. Benachrichtigung bei kritischen Zuständen.' },
  { id: 'prom-operator', label: 'Prometheus\nOperator', x: 520, y: 660, w: 115, h: 44, color: '#e8590c', group: 'eks', tooltip: 'Kubernetes Operator. Verwaltet Prometheus, Alertmanager und ServiceMonitor CRDs.' },
  { id: 'postgres-exporter', label: 'Postgres\nExporter', x: 655, y: 660, w: 110, h: 44, color: '#e8590c', group: 'eks', tooltip: 'Exportiert PostgreSQL-Metriken (Connections, Transactions, DB-Größe) für Prometheus.' },

  // Infra controllers (row 6 right)
  { id: 'autoscaler', label: 'Cluster\nAutoscaler', x: 785, y: 660, w: 110, h: 44, color: '#495057', group: 'eks', tooltip: 'Skaliert Node-Gruppen automatisch. GPU-Node: 0→1 bei Bedarf, zurück auf 0 nach Idle.' },
  { id: 'alb-controller', label: 'ALB\nController', x: 915, y: 660, w: 100, h: 44, color: '#495057', group: 'eks', tooltip: 'AWS Load Balancer Controller. Erstellt/verwaltet ALBs und Target Groups aus Ingress-Ressourcen.' },
  { id: 'external-dns', label: 'External\nDNS', x: 1035, y: 660, w: 95, h: 44, color: '#495057', group: 'eks', tooltip: 'Synchronisiert Kubernetes Ingress/Service mit Route 53 DNS-Records automatisch.' },
];

const edges: Edge[] = [
  // Users to frontends
  { from: 'users', to: 'wordpress' },
  { from: 'users', to: 'seller-ui' },
  { from: 'sellers', to: 'seller-ui' },
  { from: 'sellers', to: 'wordpress' },
  { from: 'mobile', to: 'alb-eks' },
  { from: 'mobile', to: 'wordpress' },
  { from: 'admins', to: 'admin-ui' },
  { from: 'admins', to: 'matomo' },
  { from: 'admins', to: 'wordpress' },

  // Frontends to LBs
  { from: 'seller-ui', to: 'alb-eks' },
  { from: 'wordpress', to: 'alb-wp' },
  { from: 'matomo', to: 'alb-wp' },
  { from: 'admin-ui', to: 'cloudfront' },

  // LBs to services
  { from: 'alb-eks', to: 'bff' },
  { from: 'alb-eks', to: 'seller-nginx' },
  { from: 'alb-eks', to: 'storage' },
  { from: 'alb-eks', to: 'authorization' },
  { from: 'alb-eks', to: 'payment' },
  { from: 'alb-eks', to: 'mailer' },
  { from: 'alb-eks', to: 'etcd-browser' },
  { from: 'alb-wp', to: 'wp-pod' },
  { from: 'alb-wp', to: 'matomo-pod' },
  { from: 'cloudfront', to: 's3' },
  { from: 'cloudfront', to: 'cognito' },

  // BFF connections
  { from: 'bff', to: 'storage' },
  { from: 'bff', to: 'ollama', label: 'AI' },

  // Ollama to model & GPU
  { from: 'ollama', to: 'qwen' },
  { from: 'ollama', to: 'ec2-gpu', label: 'GPU' },

  // Service to data
  { from: 'storage', to: 'postgres' },
  { from: 'storage', to: 'etcd' },
  { from: 'authorization', to: 'postgres' },
  { from: 'payment', to: 'postgres' },
  { from: 'recovery', to: 'postgres' },
  { from: 'mailer', to: 'ses' },

  // Auth
  { from: 'seller-ui', to: 'keycloak', label: 'OIDC' },
  { from: 'keycloak-cors', to: 'keycloak' },

  // etcd config
  { from: 'authorization', to: 'etcd', label: 'config' },
  { from: 'payment', to: 'etcd', label: 'config' },
  { from: 'recovery', to: 'etcd', label: 'config' },

  // Monitoring
  { from: 'grafana', to: 'prometheus' },
  { from: 'prometheus', to: 'alertmanager' },
  { from: 'postgres-exporter', to: 'postgres' },
  { from: 'prometheus', to: 'keycloak', label: 'scrape' },
  { from: 'prometheus', to: 'storage', label: 'scrape' },
  { from: 'prometheus', to: 'admin-ui', label: 'metrics' },
  { from: 'grafana', to: 'admin-ui', label: 'dashboards' },

  // Infra controllers
  { from: 'external-dns', to: 'route53' },
  { from: 'alb-controller', to: 'alb-eks' },

  // DNS
  { from: 'route53', to: 'alb-eks' },
  { from: 'route53', to: 'alb-wp' },
  { from: 'route53', to: 'cloudfront' },

  // Conversion Tracking flow
  { from: 'admin-ui', to: 'api-gateway' },
  { from: 'wordpress', to: 'api-gateway', label: 'demo + registration' },
  { from: 'bff', to: 'api-gateway', label: 'registration' },
  { from: 'api-gateway', to: 'lambda-conversions' },
  { from: 'lambda-conversions', to: 'dynamodb-conv' },
  { from: 'lambda-conversions', to: 'matomo-pod', label: 'goals' },
  { from: 'lambda-conversions', to: 'sqs-googleads' },
  { from: 'sqs-googleads', to: 'lambda-worker' },

  // Admin tools
  { from: 'wp-pod', to: 'mariadb' },
  { from: 'matomo-pod', to: 'mariadb' },
  { from: 'lambda-worker', to: 'google-ads' },
  { from: 'pgadmin', to: 'postgres' },
  { from: 'etcd-browser', to: 'etcd' },
];

function getCenter(node: Node) {
  return { x: node.x + node.w / 2, y: node.y + node.h / 2 };
}

function getEdgePoint(node: Node, targetX: number, targetY: number) {
  const cx = node.x + node.w / 2;
  const cy = node.y + node.h / 2;
  const dx = targetX - cx;
  const dy = targetY - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };

  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  const hw = node.w / 2;
  const hh = node.h / 2;

  let scale: number;
  if (absDx / hw > absDy / hh) {
    scale = hw / absDx;
  } else {
    scale = hh / absDy;
  }
  return { x: cx + dx * scale, y: cy + dy * scale };
}

export function ArchitectureDiagram() {
  const theme = useMantineTheme();
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  const connectedNodes = new Set<string>();
  if (hoveredNode) {
    connectedNodes.add(hoveredNode);
    for (const edge of edges) {
      if (edge.from === hoveredNode) connectedNodes.add(edge.to);
      if (edge.to === hoveredNode) connectedNodes.add(edge.from);
    }
  }

  const eksNodes = nodes.filter(n => n.group === 'eks');
  const eksMinX = Math.min(...eksNodes.map(n => n.x)) - 16;
  const eksMinY = Math.min(...eksNodes.map(n => n.y)) - 30;
  const eksMaxX = Math.max(...eksNodes.map(n => n.x + n.w)) + 16;
  const eksMaxY = Math.max(...eksNodes.map(n => n.y + n.h)) + 16;

  return (
    <Paper shadow="xs" p="md" withBorder style={{ overflow: 'auto' }}>
      <svg width="1260" height="750" style={{ fontFamily: theme.fontFamily, fontSize: 11 }}>
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="10" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#adb5bd" />
          </marker>
          <marker id="arrow-highlight" viewBox="0 0 10 10" refX="10" refY="5"
            markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#228be6" />
          </marker>
        </defs>

        {/* EKS cluster boundary */}
        <rect x={eksMinX} y={eksMinY} width={eksMaxX - eksMinX} height={eksMaxY - eksMinY}
          rx={8} fill="none" stroke="#20c997" strokeWidth={2} strokeDasharray="6 3" />
        <text x={eksMinX + 8} y={eksMinY + 14} fill="#20c997" fontWeight={600} fontSize={12}>
          EKS Cluster (eu-central-1)
        </text>

        {/* Edges */}
        {edges.map((edge, i) => {
          const from = nodeMap.get(edge.from)!;
          const to = nodeMap.get(edge.to)!;
          const c1 = getCenter(from);
          const c2 = getCenter(to);
          const p1 = getEdgePoint(from, c2.x, c2.y);
          const p2 = getEdgePoint(to, c1.x, c1.y);
          const midX = (p1.x + p2.x) / 2;
          const midY = (p1.y + p2.y) / 2;
          const isHighlighted = hoveredNode && (edge.from === hoveredNode || edge.to === hoveredNode);
          const isDimmed = hoveredNode && !isHighlighted;
          return (
            <g key={i} opacity={isDimmed ? 0.15 : 1}>
              <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke={isHighlighted ? '#228be6' : '#adb5bd'} strokeWidth={isHighlighted ? 2 : 1.2} markerEnd={isHighlighted ? 'url(#arrow-highlight)' : 'url(#arrow)'} />
              {edge.label && (
                <text x={midX} y={midY - 4} textAnchor="middle" fill={isHighlighted ? '#228be6' : '#868e96'} fontSize={9}>
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => {
          const lines = node.label.split('\n');
          const isDimmed = hoveredNode && !connectedNodes.has(node.id);
          return (
            <g key={node.id} style={{ cursor: 'pointer' }} opacity={isDimmed ? 0.25 : 1}
              onMouseEnter={() => setHoveredNode(node.id)} onMouseLeave={() => setHoveredNode(null)}>
              {node.tooltip && <title>{node.tooltip}</title>}
              <rect x={node.x} y={node.y} width={node.w} height={node.h}
                rx={6} fill={node.color} opacity={0.12} stroke={node.color} strokeWidth={hoveredNode === node.id ? 2.5 : 1.5} />
              {lines.map((line, li) => (
                <text key={li} x={node.x + node.w / 2} y={node.y + node.h / 2 + (li - (lines.length - 1) / 2) * 13}
                  textAnchor="middle" dominantBaseline="central" fill={node.color} fontWeight={li === 0 ? 600 : 400}>
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </Paper>
  );
}
